import React, { useState } from "react";
import { FaAngleDoubleRight, FaMobileAlt, FaTabletAlt, FaDesktop } from "react-icons/fa";
import img from "../../src/sample_food.jpg"

// Sample best-selling products data
const bestSellingProducts = [
    {
        id: 1,
        label: "Best Seller",
        imageUrl: img,
        name: "Product 1",
        originalPrice: 200,
        discountPrice: 150,
    },
    {
        id: 2,
        label: "Top Rated",
        imageUrl: img,
        name: "Product 2",
        originalPrice: 250,
        discountPrice: 200,
    },
    {
        id: 3,
        label: "Trending",
        imageUrl: img,
        name: "Product 3",
        originalPrice: 300,
        discountPrice: 250,
    },
    {
        id: 4,
        label: "Must Buy",
        imageUrl: img,
        name: "Product 4",
        originalPrice: 400,
        discountPrice: 350,
    },
];

const DEVICE_SIZES = {
    mobile: { width: 375, height: 812, scale: 0.65, label: 'Mobile', icon: FaMobileAlt },
    tablet: { width: 768, height: 1024, scale: 0.55, label: 'Tablet', icon: FaTabletAlt },
    desktop: { width: 1280, height: 800, scale: 0.7, label: 'Desktop', icon: FaDesktop },
};

const DynamicBestSellingSection = ({ formData = {} }) => {
    const [deviceSize, setDeviceSize] = useState('mobile');

    // Fallback dynamic classes (if admin hasn't set a particular option)
    const isCustomColor = (v) => typeof v === 'string' && (v.startsWith('#') || v.startsWith('rgb'));
    const containerBackground = formData.containerBackground?.value || formData.containerBackground || "#f3f4f6";
    const containerBorderRadius = formData.containerBorderRadius?.value || "rounded-xl";
    const headerTitleColor = formData.headerTitleColor?.value || "text-gray-900";
    const headerTitleFont = formData.headerTitleFont?.value || "text-2xl font-bold";
    const headerDropShadow = formData.headerDropShadow?.value || "drop-shadow-sm";
    const iconHover = formData.iconHover?.value || "hover:text-blue-600";
    const iconTransition = formData.iconTransition?.value || "transition-colors duration-300";
    const cardHoverScale = formData.cardHoverScale?.value || "hover:scale-105";
    const cardHoverShadow = formData.cardHoverShadow?.value || "hover:shadow-md";
    const cardPadding = formData.cardPadding?.value || "p-3";
    const cardBorderRadius = formData.cardBorderRadius?.value || "rounded-xl";
    const cardDiscountTextColor = formData.cardDiscountTextColor?.value || "text-gray-500";
    const sectionName = formData.sectionName?.label || "Dummy Section Name";

    const priceDetailsLayout = formData.priceDetailsLayout?.value || 'side-by-side';
    const productInfoStyle = formData.productInfoStyle?.value || 'default';
    const displayCount = formData.displayCount || 4;

    // Card Styles - Get value from formData safely with optional chaining
    const cardStyle = formData.cardStyle?.value || "bg-white";
    const borderWidth = formData.borderWidth?.value || "border";
    const borderColor = formData.borderColor?.value || "border-gray-200";
    const hoverEffect = formData.hoverEffect?.value || "";
    const animation = formData.animation?.value || "";
    const imageBorderRadius = formData.imageBorderRadius?.value || "rounded-lg";
    const imagePadding = formData.imagePadding?.value || "p-2";

    // Get section type label
    const sectionType = formData.sectionType?.value || formData.sectionType || 'grid-2col';
    const sectionTypeLabel = formData.sectionType?.label || sectionType;

    // Merge additional dynamic card classes from admin settings
    const cardDynamicClasses = `
        ${cardStyle}
        ${borderWidth}
        ${borderColor}
        ${hoverEffect}
        ${animation}
        ${cardBorderRadius}
        ${cardHoverScale}
        ${cardHoverShadow}
        cursor-pointer
        transition-transform
        transform
        shadow-sm
    `;

    // Determine grid columns based on device size
    const getGridCols = () => {
        if (deviceSize === 'mobile') return 'grid-cols-2';
        if (deviceSize === 'tablet') return 'grid-cols-3';
        return 'grid-cols-2 sm:grid-cols-3 md:grid-cols-4';
    };

    // Get products limited by displayCount
    const visibleProducts = bestSellingProducts.slice(0, Math.min(displayCount, bestSellingProducts.length));

    const deviceConfig = DEVICE_SIZES[deviceSize];
    const deviceWidth = deviceConfig.width;
    const deviceHeight = deviceConfig.height;
    const deviceScale = deviceConfig.scale;

    return (
        <div className="flex flex-col items-center w-full">
            {/* Device Size Toggle */}
            <div className="flex items-center gap-2 mb-4 bg-white rounded-lg shadow-sm border border-gray-200 p-1">
                {Object.entries(DEVICE_SIZES).map(([key, config]) => {
                    const IconComponent = config.icon;
                    return (
                        <button
                            key={key}
                            onClick={() => setDeviceSize(key)}
                            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
                                deviceSize === key
                                    ? 'bg-blue-500 text-white shadow-sm'
                                    : 'text-gray-500 hover:bg-gray-100'
                            }`}
                        >
                            <IconComponent className="w-3.5 h-3.5" />
                            {config.label}
                        </button>
                    );
                })}
            </div>

            {/* Section Type Badge */}
            <div className="mb-3 flex items-center gap-2">
                <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full font-medium">
                    Layout: {sectionTypeLabel}
                </span>
                <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-medium">
                    Products: {displayCount}
                </span>
                <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-medium">
                    {deviceConfig.label}: {typeof deviceWidth === 'number' ? `${deviceWidth}×${deviceHeight}` : 'Full'}
                </span>
            </div>

            {/* Device Frame - Zoomed Out */}
            <div style={{
                width: `${deviceWidth * deviceScale}px`,
                height: `${deviceHeight * deviceScale}px`,
                maxWidth: '100%',
                overflow: 'hidden',
                position: 'relative',
            }}>
                <div 
                    className={`${
                        deviceSize === 'mobile' ? 'border-[8px] border-gray-800 rounded-[32px] shadow-2xl' 
                        : deviceSize === 'tablet' ? 'border-[10px] border-gray-700 rounded-[20px] shadow-2xl' 
                        : 'border border-gray-300 rounded-lg shadow-lg'
                    } bg-white overflow-hidden`}
                    style={{ 
                        width: `${deviceWidth}px`,
                        height: `${deviceHeight}px`,
                        transform: `scale(${deviceScale})`,
                        transformOrigin: 'top left',
                    }}
                >
                    {/* Phone status bar for mobile */}
                    {deviceSize === 'mobile' && (
                        <div className="bg-gray-800 flex justify-center items-center py-1.5 px-4">
                            <div className="w-24 h-5 bg-gray-900 rounded-full"></div>
                        </div>
                    )}

                    {/* Tablet camera for tablet */}
                    {deviceSize === 'tablet' && (
                        <div className="bg-gray-700 flex justify-center items-center py-1">
                            <div className="w-3 h-3 bg-gray-900 rounded-full"></div>
                        </div>
                    )}

                    {/* Desktop browser bar */}
                    {deviceSize === 'desktop' && (
                        <div className="bg-gray-100 border-b border-gray-200 flex items-center gap-2 px-4 py-2">
                            <div className="flex gap-1.5">
                                <div className="w-3 h-3 rounded-full bg-red-400"></div>
                                <div className="w-3 h-3 rounded-full bg-yellow-400"></div>
                                <div className="w-3 h-3 rounded-full bg-green-400"></div>
                            </div>
                            <div className="flex-1 mx-4">
                                <div className="bg-white border border-gray-300 rounded-md px-3 py-1 text-xs text-gray-400 max-w-sm">
                                    yourstore.zuntly.com
                                </div>
                            </div>
                        </div>
                    )}
                
                    <div
                      className={`w-full p-4 ${containerBorderRadius} ${isCustomColor(containerBackground) ? '' : containerBackground} overflow-auto`}
                      style={{
                        maxHeight: deviceSize === 'mobile' ? '720px' : deviceSize === 'tablet' ? '950px' : '720px',
                        ...(isCustomColor(containerBackground) ? { backgroundColor: containerBackground } : {})
                      }}>
                        {/* Header Section */}
                        <div className="flex justify-between items-center mb-4 text-left">
                            <h2 className={`${headerTitleFont} ${headerTitleColor} tracking-tight ${headerDropShadow}`}>
                                {sectionName}
                            </h2>
                            <FaAngleDoubleRight className={`${headerTitleColor} text-xl ${iconHover} ${iconTransition} cursor-pointer`} />
                        </div>

                        {/* Products Grid */}
                        <div className={`grid ${getGridCols()} gap-3`}>
                            {visibleProducts.map((product, index) => (
                                <div key={index} className={`relative mb-2 ${cardDynamicClasses}`}>
                                    <div>
                                        <div className={`w-full h-28 sm:h-36 overflow-hidden relative ${imagePadding}`}>
                                            <img
                                                src={product.imageUrl}
                                                alt={product.name}
                                                className={`w-full h-full object-cover ${imageBorderRadius}`}
                                            />
                                        </div>
                                        <div className={`${cardPadding} ${productInfoStyle === 'card' ? 'bg-white shadow-md rounded-lg border border-gray-200 p-2' : ''}`}>
                                            <h3 className="text-sm font-semibold text-gray-700">{product.name}</h3>
                                            <p className="text-[10px] text-gray-500 mt-0.5">250gms</p>
                                            <div className={`mt-1 ${priceDetailsLayout === 'side-by-side' ? 'flex justify-between items-center' : 'flex flex-col'}`}>
                                                <p className={`text-[10px] line-through ${cardDiscountTextColor}`}>&#8377;{product.originalPrice}</p>
                                                <p className="text-sm font-bold text-gray-700">
                                                    &#8377;{product.discountPrice}
                                                    <span className="text-[9px] text-gray-400 ml-0.5">
                                                        ({(((product.originalPrice - product.discountPrice) / product.originalPrice) * 100).toFixed(0)}% off)
                                                    </span>
                                                </p>
                                            </div>
                                            {/* ADD Button Preview */}
                                            <button className="w-full mt-1.5 py-1 bg-blue-500 text-white text-[10px] font-bold rounded-md">
                                                ADD
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Phone bottom bar for mobile */}
                    {deviceSize === 'mobile' && (
                        <div className="bg-gray-800 flex justify-center py-2">
                            <div className="w-28 h-1 bg-gray-600 rounded-full"></div>
                        </div>
                    )}

                    {/* Tablet bottom bar */}
                    {deviceSize === 'tablet' && (
                        <div className="bg-gray-700 flex justify-center py-1.5">
                            <div className="w-16 h-1 bg-gray-500 rounded-full"></div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default DynamicBestSellingSection;
